/**
 * Custom Email Service
 *
 * All email sending is handled by Cloud Functions triggered by Firestore writes
 * to the emailTokens collection. This service creates tokens that trigger
 * the appropriate Resend-based Cloud Function to send the email.
 *
 * This replaces the previous Firebase Auth native email system and the
 * EmailJS stub with a unified token-based Resend flow.
 */

import { doc, setDoc, getDoc, deleteDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

class CustomEmailService {
  /**
   * Generate verification token and store in Firestore
   */
  async generateVerificationToken(userId, email, type = 'email-verification') {
    const token = uuidv4();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

    const tokenData = {
      token,
      userId,
      email,
      type,
      createdAt: new Date(),
      expiresAt,
      used: false
    };

    // Store token in Firestore — this triggers the Cloud Function
    await setDoc(doc(db, 'emailTokens', token), tokenData);

    return token;
  }

  /**
   * Send email verification using the Resend Cloud Function.
   * Creates an email-verification token in emailTokens, which triggers
   * the sendCustomEmailVerification Cloud Function.
   */
  async sendEmailVerification(user) {
    try {
      // Generate verification token
      const token = await this.generateVerificationToken(user.uid, user.email, 'email-verification');

      return { success: true, token };
    } catch (error) {
      console.error('Custom email verification failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send password reset email using the Resend Cloud Function.
   * Creates a password-reset token in emailTokens, which triggers
   * the sendCustomPasswordReset Cloud Function.
   */
  async sendPasswordResetEmail(email) {
    try {
      // Generate reset token (userId is null — resolved by email lookup in the Cloud Function)
      const token = await this.generateVerificationToken(null, email, 'password-reset');

      return { success: true, token };
    } catch (error) {
      console.error('Custom password reset failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Verify token and mark email as verified
   */
  async verifyEmailToken(token) {
    try {
      // Get token from Firestore
      const tokenDoc = await getDoc(doc(db, 'emailTokens', token));

      if (!tokenDoc.exists()) {
        return { success: false, error: 'Invalid verification token' };
      }

      const tokenData = tokenDoc.data();

      // Check if token is expired
      if (new Date() > tokenData.expiresAt.toDate()) {
        await deleteDoc(doc(db, 'emailTokens', token));
        return { success: false, error: 'Verification token has expired' };
      }

      // Check if token is already used
      if (tokenData.used) {
        return { success: false, error: 'Verification token has already been used' };
      }

      // Mark token as used
      await setDoc(doc(db, 'emailTokens', token), { ...tokenData, used: true });

      // Update user's emailVerified status in your user collection
      if (tokenData.type === 'email-verification') {
        await setDoc(doc(db, 'users', tokenData.userId), {
          emailVerified: true,
          emailVerifiedAt: new Date()
        }, { merge: true });
      }

      return {
        success: true,
        userId: tokenData.userId,
        email: tokenData.email,
        type: tokenData.type
      };

    } catch (error) {
      console.error('Token verification failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clean up expired tokens (run periodically)
   */
  async cleanupExpiredTokens() {
    try {
      // Note: In a real app, you'd use Firebase Functions to clean up expired tokens
      console.log('Token cleanup would run here');
    } catch (error) {
      console.error('Token cleanup failed:', error);
    }
  }
}

export default new CustomEmailService();
